import { BeneficiaryDetailsStyle } from 'beneficiaries/styles/beneficiary-details.styles';
import { useMixpanel } from 'context/mix-panel/mix-panel-context';
import { MixpanelDomain, MixpanelEventNames } from 'context/mix-panel/mix-panel-types';
import moment, { Moment } from 'moment';
import { getPolicyById } from 'policies/actions/get-policy-by-id';
import { getPolicyStartDatePreview } from 'policies/actions/get-policy-start-date-preview';
import { movePolicyStartDate } from 'policies/actions/move-policy-start-date';
import { Policy } from 'policies/domain/policy';
import { PolicyBillingFrequency } from 'policies/domain/policy-billing';
import { PolicyStatus } from 'policies/domain/policy-status';
import { StartDateAdjustmentDetails, LedgerAdjustment } from 'policies/domain/start-date-adjustments';
import { ManagementView } from 'policy-management/policy-management-scene';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormWrapperStyle } from 'rootstrap/components-old/root-schema-form/root-schema-form';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { SuccessButton } from 'rootstrap/components/button/styles';
import { ErrorAlert } from 'rootstrap/components/error-alert';
import { DatePickerField } from 'rootstrap/components/forms/new-fields/date-picker-field';
import { BillingDaySelectField } from 'rootstrap/components/forms/new-fields/extended-components/billing-day-select-field';
import { SelectField } from 'rootstrap/components/forms/new-fields/select-field';
import { InputFieldDisplayProperties } from 'rootstrap/components/forms/new-fields/input-field';
import { ActiveElement } from 'rootstrap/components/forms/new-fields/utils';
import NewSpinner, { AnimationTypes, SpinnerSize } from 'rootstrap/components/spinner/new-spinner';
import { useEmbedParamsContext } from 'shared/embed-params-context';
import { usePromise, usePromiseLazy } from 'shared/hooks/promise';
import { useSiteConfigContext } from 'style-context';
import styled from 'styled-components';
import { Colors } from 'rootstrap/global-styles/colors';
import { Spacing } from 'rootstrap/global-styles';
import { getColor, ProductModuleDefinitionEmbeddedConfig } from 'site-config';
import { devices } from 'rootstrap/global-styles/devices';

interface Props {
  policy: Policy | undefined;
  isLoadingPolicy: boolean;
  setPolicy: (policy: Policy) => void;
  setView: (params: { view: ManagementView }) => void;
}

enum MoveStartDateInputs {
  StartDate = 'startDate',
  BillingDay = 'billingDay',
  BillingMonth = 'billingMonth',
}

interface MoveStartDateFormData {
  [MoveStartDateInputs.StartDate]?: Moment;
  [MoveStartDateInputs.BillingDay]?: number;
  [MoveStartDateInputs.BillingMonth]?: number;
}

const billingMonths = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const noDataMessage = 'Selection resulted in no data because there was no start date or billing day change';

const formatCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: currency || 'ZAR' }).format(value / 100);
};

export const MoveStartDateScene = (props: Props) => {
  const { embedParams } = useEmbedParamsContext();
  const { track } = useMixpanel();
  const { environment, organizationId, auth } = embedParams;
  const { policy, isLoadingPolicy, setPolicy, setView } = props;
  const { siteConfig } = useSiteConfigContext();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [activeElement, setActiveElement] = useState<ActiveElement>({
    elementId: MoveStartDateInputs.StartDate,
  });

  const [startDateAdjustments, setStartDateAdjustments] = useState<StartDateAdjustmentDetails | undefined>();

  const form = useForm<MoveStartDateFormData>({
    mode: 'onChange',
  });

  const selectedStartDate = form.watch(MoveStartDateInputs.StartDate);
  const selectedBillingDay = form.watch(MoveStartDateInputs.BillingDay);
  const selectedBillingMonth = form.watch(MoveStartDateInputs.BillingMonth);

  useEffect(() => {
    track(MixpanelEventNames.PageSelected, {
      domain: MixpanelDomain.Management,
      pageName: 'Move start date',
    });
  }, []);

  const {
    isLoading: isLoadingPreview,
    error: previewError,
  } = usePromise(async () => {
    if (!selectedStartDate || !policy) {
      setStartDateAdjustments(undefined);
      return;
    }

    const formattedDate = moment(selectedStartDate).format('YYYY-MM-DD');

    const adjustments = await getPolicyStartDatePreview({
      auth,
      environment,
      organizationId,
      policyId: policy.policyId,
      newStartDate: formattedDate,
      newBillingDay: selectedBillingDay,
      newBillingMonth: selectedBillingMonth,
    });

    setStartDateAdjustments(adjustments);
  }, [selectedStartDate, selectedBillingDay, selectedBillingMonth]);

  const {
    execute: executeMove,
    error: moveError,
    isLoading: isMoving,
  } = usePromiseLazy(async () => {
    if (!policy || !selectedStartDate) {
      throw new Error('Please select a new start date');
    }

    const formattedDate = moment(selectedStartDate).format('YYYY-MM-DD');

    await movePolicyStartDate({
      auth,
      environment,
      organizationId,
      policyId: policy.policyId,
      startDate: formattedDate,
      billingDay: selectedBillingDay,
      billingMonth: selectedBillingMonth,
    });

    track(MixpanelEventNames.PolicyStartDateMoved, {
      domain: MixpanelDomain.Management,
      policyId: policy.policyId,
      policyNumber: policy.policyNumber,
    });

    const updatedPolicy = await getPolicyById({ auth, environment, organizationId, policyId: policy.policyId });
    setPolicy(updatedPolicy);

    setIsRedirecting(true);
    setView({ view: ManagementView.PolicyDetails });
  }, [policy, selectedStartDate, selectedBillingDay, selectedBillingMonth]);

  const isPendingPolicy =
    policy?.status === PolicyStatus.Pending || policy?.status === PolicyStatus.PendingInitialPayment;

  if (!isLoadingPolicy && !policy) {
    return <ErrorAlert title='Policy not found' error={new Error('Policy could not be loaded. Please go back and try again.')} />;
  }

  if (!isLoadingPolicy && policy && !isPendingPolicy) {
    return (
      <ErrorAlert
        title='Cannot move start date'
        error={new Error('Start date can only be moved for policies in a pending state.')}
      />
    );
  }

  const displayError = previewError?.message !== noDataMessage ? previewError : moveError;
  const hasNoChanges =
    previewError?.message === noDataMessage ||
    (startDateAdjustments && startDateAdjustments.ledgerAdjustments.length === 0);

  const displayProperties = {
    activeElement,
    setActiveElement,
    nextComponentName: MoveStartDateInputs.StartDate,
  } as InputFieldDisplayProperties;

  return (
    <FormWrapperStyle>
      {displayError && <ErrorAlert title='Failed to move start date' error={displayError} />}
      <form onSubmit={form.handleSubmit(() => executeMove())}>
        <BeneficiaryDetailsStyle style={{ paddingBottom: 60 }}>
          <DatePickerField
            label='Select a new start date'
            name={MoveStartDateInputs.StartDate}
            form={form}
            placeholder=''
            validators={[
              {
                validation: {
                  type: ValidationTypes.REQUIRED,
                },
              },
            ]}
            isTouched={true}
            disableScrollToElement={false}
            hideDivider={true}
            disableNextButton={true}
            disableActiveElement={true}
            displayProperties={displayProperties}
          />

          {policy && (
            <SummaryCard siteConfig={siteConfig}>
              <SummaryRow>
                <SummaryLabel>Current start date</SummaryLabel>
                <SummaryValue siteConfig={siteConfig}>{policy.startDate.format('DD MMM YYYY')}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Current billing day</SummaryLabel>
                <SummaryValue siteConfig={siteConfig}>{policy.billingDay}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Current balance</SummaryLabel>
                <SummaryValue siteConfig={siteConfig}>
                  {formatCurrency(policy.balance, policy.currency)}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Monthly premium</SummaryLabel>
                <SummaryValue siteConfig={siteConfig}>
                  {formatCurrency(policy.monthlyPremium, policy.currency)}
                </SummaryValue>
              </SummaryRow>
            </SummaryCard>
          )}

          {policy?.billingFrequency === PolicyBillingFrequency.Monthly && (
            <BillingDaySelectField
              label='Select a new billing day'
              name={MoveStartDateInputs.BillingDay}
              form={form}
              placeholder=''
              clearable={true}
              validators={[]}
              isTouched={true}
              disableScrollToElement={false}
              hideDivider={true}
              disableNextButton={true}
              disableActiveElement={true}
              displayProperties={displayProperties}
            />
          )}

          {policy?.billingFrequency === PolicyBillingFrequency.Yearly && (
            <SelectField
              clearable={true}
              options={billingMonths}
              label='Select a new billing month'
              name={MoveStartDateInputs.BillingMonth}
              form={form}
              validators={[]}
              placeholder=''
              isTouched={true}
              disableScrollToElement={false}
              hideDivider={true}
              disableNextButton={true}
              disableActiveElement={true}
              displayProperties={displayProperties}
            />
          )}

          {isLoadingPreview && selectedStartDate && (
            <LoadingContainer>
              <NewSpinner animation={AnimationTypes.Border} size={SpinnerSize.sm} color={Colors.Body} />
              <span style={{ marginLeft: 8 }}>Loading preview...</span>
            </LoadingContainer>
          )}

          {!isLoadingPreview &&
            startDateAdjustments &&
            startDateAdjustments.ledgerAdjustments.length > 0 && (
              <TransactionCard siteConfig={siteConfig}>
                <TransactionHeader>Transaction history</TransactionHeader>
                <p style={{ fontSize: 14, color: Colors.MediumEmphasis, marginBottom: 16 }}>
                  The following ledger entry will be added to account for the start date change
                </p>
                <TransactionTable siteConfig={siteConfig}>
                  <thead>
                    <tr>
                      <th>Effective date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {startDateAdjustments.ledgerAdjustments.map((adjustment: LedgerAdjustment, index: number) => (
                      <tr key={index}>
                        <td>
                          {startDateAdjustments.deferUpdateUntil
                            ? startDateAdjustments.deferUpdateUntil.format('DD MMM YYYY')
                            : moment().format('DD MMM YYYY')}
                        </td>
                        <td>{adjustment.description}</td>
                        <td>{formatCurrency(adjustment.amount, adjustment.currency)}</td>
                        <td>{formatCurrency(adjustment.balance, adjustment.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </TransactionTable>
              </TransactionCard>
            )}

          {!isLoadingPreview && hasNoChanges && selectedStartDate && (
            <InfoBanner siteConfig={siteConfig}>
              The selected start date and billing day resulted in no billing changes. Either the billing details are the
              same as the current policy details or pro-rata is disabled. Change your selection to preview the policy
              billing changes or apply the change if it still applies.
            </InfoBanner>
          )}

          {!isLoadingPreview && startDateAdjustments?.deferUpdateUntil && (
            <InfoBanner siteConfig={siteConfig}>
              This update will be deferred to {startDateAdjustments.deferUpdateUntil.format('DD MMM YYYY')} due to a
              recent premium collection for this policy.
            </InfoBanner>
          )}

          <button style={{ display: 'none' }} ref={submitButtonRef} type='submit' />
          <SuccessButton
            id='submit-move-start-date'
            disabled={!selectedStartDate || isLoadingPreview || isMoving || isRedirecting}
            siteConfig={siteConfig}
            onClick={() => submitButtonRef.current?.click()}
            fullWidth={true}
          >
            {isMoving && (
              <span style={{ marginRight: '10px' }}>
                <NewSpinner animation={AnimationTypes.Border} size={SpinnerSize.sm} color={'white'} />
              </span>
            )}
            <span>Apply</span>
          </SuccessButton>
        </BeneficiaryDetailsStyle>
      </form>
    </FormWrapperStyle>
  );
};

const SummaryCard = styled.div<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  padding: 20px 25px;
  margin-top: 20px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: ${({ siteConfig }) => getColor({ siteConfig, color: 'light' })};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const SummaryLabel = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${Colors.Body};
`;

const SummaryValue = styled.span<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  font-size: 16px;
  font-weight: 600;
  color: ${({ siteConfig }) => getColor({ siteConfig, color: 'primary' })};
`;

const TransactionCard = styled.div<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  padding: 20px 25px;
  margin-top: 20px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: ${({ siteConfig }) => getColor({ siteConfig, color: 'light' })};
`;

const TransactionHeader = styled.p`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 8px;
  color: ${Colors.Body};
`;

const TransactionTable = styled.table<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 10px 0;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  th {
    font-weight: 600;
    color: ${Colors.MediumEmphasis};
  }

  td {
    color: ${Colors.Body};
  }

  td:nth-child(3),
  th:nth-child(3),
  td:nth-child(4),
  th:nth-child(4) {
    text-align: right;
  }

  @media ${devices.mobileL} {
    font-size: 12px;
  }
`;

const InfoBanner = styled.div<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  margin-top: 20px;
  padding: 16px 25px;
  border-radius: 10px;
  background: ${({ siteConfig }) => getColor({ siteConfig, color: 'light' })};
  font-size: 14px;
  color: ${Colors.MediumEmphasis};
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.md} 0;
  font-size: 16px;
  color: ${Colors.MediumEmphasis};
`;
